import { Input } from "../src/components/ui/input.jsx";

import React, { useState, useEffect } from "react";
import { TestCase } from "../src/entities/TestCase.js";
import { Card, CardContent, CardHeader, CardTitle } from "../src/components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../src/components/ui/tabs.jsx";
import { Button } from "../src/components/ui/button.jsx";
import { Badge } from "../src/components/ui/badge.jsx";
import { Save, Eye, Copy, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "../src/utils/index.js";

import TestCaseForm from "../Components/forms/TestCaseForm.jsx";
import ConfigForm from "../Components/forms/ConfigForm.jsx";
import JsonPreview from "../Components/preview/JsonPreview.jsx";
import PresetSelector from "../Components/presets/PresetSelector.jsx";
import { saveEvaluation } from "../src/services/evaluationsApi.js";

export default function CreateTest() {
  const navigate = useNavigate();
  const [testCase, setTestCase] = useState({
    title: "",
    user_input: "",
    expected_response_answer: "",
    expected_response_question: "",
    actual_response_answer: "",
    actual_response_question: "",
    scenario_auth: "anonymous",
    status: "draft"
  });

  const [presetType, setPresetType] = useState("custom");
  const [collectionsText, setCollectionsText] = useState("");

  const [config, setConfig] = useState({
    org_id: "",
    project_id: `proj_eval_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,8)}`,
    conversation_id: `conv_eval_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,8)}`,
    country: "Australia",
    first_name: "",
    last_name: "",
    email: "",
    state: "",
    user_id: "",
    workflow: {
      next_action: "",
      execution_logs: {},
      intent: ""
    },
    project: {
      council_name: "",
      address: "",
      zone: "",
      zone_description: "",
      prop_id: "",
      active_precinct: "",
      application_name: "",
      development_type: "",
      planning_pathway: "",
      lot_width: "",
      lot_size: "",
      minimum_lot_size: "",
      project_size: "",
      building_height: "",
      maximum_building_height: "",
      sub_division: "",
      front_setback: "",
      side_setback: "",
      rear_setback: "",
      is_bushfire_zone: "",
      is_flood_zone: "",
      is_heritage_zone: "",
      matched_pathways: [],
      failed_pathways: [],
      additional_criteria: "",
      flags: {},
      dwelling_density: "",
      documents: [],
      uploaded_documents: [],
      is_corner_lot: false,
      is_battle_axe_lot: false
    },
    policies: {
      state: {
        SEP: "",
        SEP_public_link: "",
        pol_min_lot_size: "",
        pol_min_lot_width: "",
        pol_max_FSR: "",
        pol_max_building_height: "",
        pol_max_building_depth: "",
        pol_max_building_separation: "",
        pol_max_site_coverage: "",
        pol_allowed_use_types: "",
        pol_basix_energy_target: "",
        pol_basix_water_target: "",
        pol_bushfire_attack_level: "",
        pol_coastal_vulnerability: "",
        pol_foreshore_building_line: "",
        pol_front_setback: "",
        pol_gsyd_tree_canopy_2019: "",
        pol_gsyd_tree_canopy_2022: "",
        pol_housing_prod_contribution: "",
        pol_is_flood_prone: "",
        pol_is_heritage_item: "",
        pol_land_reservation_acquisition: "",
        pol_landscaped_pct: "",
        pol_privacy_separation: "",
        pol_rear_setback: "",
        pol_regional_plan_boundary: "",
        pol_salinity: "",
        pol_side_setback: "",
        pol_solar_access: "",
        pol_acid_sulfate_class: ""
      },
      local: {
        DCP: "",
        DCP_public_link: "",
        LEP: "",
        LEP_public_link: "",
        pol_min_lot_size: "",
        pol_max_FSR: "",
        pol_max_building_depth: "",
        pol_max_building_height: "",
        pol_max_building_separation: "",
        pol_max_site_coverage: "",
        pol_is_heritage_area: "",
        pol_land_zone: "",
        pol_landscaped_pct: "",
        pol_land_council: "",
        pol_provisions: "",
        pol_permitted_use: ""
      }
    }
  });

  const [activeTab, setActiveTab] = useState("basics");

  // Expose tab switching function to window for tutorial integration
  useEffect(() => {
    window.setCreateTestTab = (tabName) => {
      console.log(`Tutorial requesting tab switch to: ${tabName}`);
      setActiveTab(tabName);
      // Return a promise that resolves after the tab content is rendered
      return new Promise(resolve => {
        setTimeout(() => {
          console.log(`Tab ${tabName} content should now be rendered`);
          resolve();
        }, 500); // Increased delay to ensure DOM is fully updated
      });
    };

    // Cleanup
    return () => {
      delete window.setCreateTestTab;
    };
  }, []);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fetchSignal, setFetchSignal] = useState(null);

  const handlePresetSelect = (presetData, nextPresetType, scenarioAuth) => {
    const newProjectId = `proj_eval_${(crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2,10)}`)}`;
    const newConversationId = `conv_eval_${(crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2,10)}`)}`;
    const withIds = { ...presetData, project_id: newProjectId, conversation_id: newConversationId };
    setConfig(withIds);
    setPresetType(nextPresetType);
    if (scenarioAuth) {
      setTestCase((prev) => ({ ...prev, scenario_auth: scenarioAuth }));
    }
    const isAddressPreset = nextPresetType === 'address_preset';
    if (isAddressPreset && withIds?.project?.address && withIds?.org_id) {
      setFetchSignal({
        nonce: `${Date.now()}-${Math.random()}`,
        address: withIds.project.address,
      });
      setActiveTab('project');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const userName = [config.first_name, config.last_name].filter(Boolean).join(' ').trim();
      const collections = collectionsText.split(',').map(s => s.trim()).filter(Boolean);

      const payload = {
        title: testCase.title,
        chat_input: testCase.user_input,
        expected_answer: testCase.expected_response_answer,
        expected_question: testCase.expected_response_question,
        actual_answer: testCase.actual_response_answer,
        actual_question: testCase.actual_response_question,
        user_config: config,
        meta: {
          title: testCase.title,
          preset_type: presetType,
          created_date: new Date().toISOString(),
          // Responsibility / user info
          user_oid: config.user_id || '',
          user_name: userName || '',
          user_email: config.email || '',
          auth_status: testCase.scenario_auth || 'anonymous',
          // Program context for filtering/quantification
          state: config.state || '',
          planning_pathway: config?.project?.planning_pathway || '',
          council_name: config?.project?.council_name || '',
          collections,
        }
      };

      // Save locally (existing behavior)
      await TestCase.create({ ...testCase, config_data: config, collections });

      // Persist to backend (Cosmos)
      await saveEvaluation(payload);

      navigate(createPageUrl("TestCases"));
    } catch (error) {
      console.error("Error saving test case:", error);
      alert(`Failed to save: ${error.message}`);
    }
    setIsSaving(false);
  };

  const handleCopyJson = () => {
    const userName = [config.first_name, config.last_name].filter(Boolean).join(' ').trim();
    const collections = collectionsText.split(',').map(s => s.trim()).filter(Boolean);

    const topLevel = {
      title: testCase.title,
      chat_input: testCase.user_input,
      expected_answer: testCase.expected_response_answer,
      expected_question: testCase.expected_response_question,
      actual_answer: testCase.actual_response_answer,
      actual_question: testCase.actual_response_question,
      user_config: config,
      meta: {
        title: testCase.title,
        preset_type: presetType,
        created_date: new Date().toISOString(),
        user_oid: config.user_id || '',
        user_name: userName || '',
        user_email: config.email || '',
        auth_status: testCase.scenario_auth || 'anonymous',
        state: config.state || '',
        planning_pathway: config?.project?.planning_pathway || '',
        council_name: config?.project?.council_name || '',
        collections,
      }
    };
    navigator.clipboard.writeText(JSON.stringify(topLevel, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getValueByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  };

  // Base required fields for any state (no extra state-specific requirements)
  const BASE_REQUIRED_FIELDS = [
    'org_id',
    'state',
    'project.council_name',
    'project.address',
    'project_id',
    'conversation_id',
    'user_id',
  ];

  const isConfigComplete = BASE_REQUIRED_FIELDS.every((path) => {
    const val = getValueByPath(config, path);
    return typeof val === 'string' ? val.trim() !== '' : Boolean(val);
  });

  const hasOneExpected = Boolean(
    (typeof testCase.expected_response_answer === 'string' && testCase.expected_response_answer.trim()) ||
    (typeof testCase.expected_response_question === 'string' && testCase.expected_response_question.trim())
  );

  const isValid = Boolean(
    testCase.title &&
    testCase.user_input &&
    hasOneExpected &&
    isConfigComplete
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">Create Test Case</h1>
            <p className="text-slate-600 dark:text-slate-400">Build evaluation scenarios for your AI application</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsPreviewOpen(!isPreviewOpen)}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {isPreviewOpen ? 'Hide' : 'Show'} JSON
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyJson}
              className="flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy JSON'}
            </Button>
            {/* Add a data-tour attribute so the tutorial can highlight the Save button */}
            <Button
              onClick={handleSave}
              data-tour="create-save"
              disabled={!isValid || isSaving}
              className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Test Case'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-xl font-bold text-slate-800 dark:text-slate-100">Test Configuration</CardTitle>
                  <Badge variant={isConfigComplete ? "default" : "secondary"} className={isConfigComplete ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"}>
                    {isConfigComplete ? "Config Complete" : "Config Incomplete"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start p-1 bg-slate-50 dark:bg-slate-800/50 m-6 mb-0" data-tour="create-tabs">
                    <TabsTrigger value="basics" data-tour="tab-basics">Test Details</TabsTrigger>
                    <TabsTrigger value="presets" data-tour="tab-presets">Presets</TabsTrigger>
                    <TabsTrigger value="user" data-tour="tab-user">User Config</TabsTrigger>
                    <TabsTrigger value="project" data-tour="tab-project">Project</TabsTrigger>
                    <TabsTrigger value="policies" data-tour="tab-policies">Policies</TabsTrigger>
                  </TabsList>

                  <div className="p-6">
                    <TabsContent value="basics" className="mt-0">
                      <TestCaseForm testCase={testCase} onChange={setTestCase} />
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Collections (optional)</label>
                        <Input
                          placeholder="Comma-separated, e.g. qa, logged_in, council:Woollahra"
                          value={collectionsText}
                          onChange={(e) => setCollectionsText(e.target.value)}
                          className="w-full"
                          data-tour="create-collections"
                        />
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Used to group cases. You can filter by these on the Test Cases page.</p>
                      </div>
                    </TabsContent>

                    <TabsContent value="presets" className="mt-0">
                      <PresetSelector onSelect={handlePresetSelect} currentConfig={config} currentPresetType={presetType} onScenarioAuthChange={(scenarioAuth) => setTestCase((prev) => ({ ...prev, scenario_auth: scenarioAuth }))} />
                    </TabsContent>

                    <TabsContent value="user" className="mt-0">
                      <ConfigForm 
                        config={config} 
                        onChange={setConfig} 
                        section="user"
                      />
                    </TabsContent>

                    <TabsContent value="project" className="mt-0">
                      <ConfigForm 
                        config={config} 
                        onChange={setConfig} 
                        section="project"
                        fetchSignal={fetchSignal}
                        onFetchSignalConsumed={() => setFetchSignal(null)}
                      />
                    </TabsContent>

                    <TabsContent value="policies" className="mt-0">
                      <ConfigForm 
                        config={config} 
                        onChange={setConfig} 
                        section="policies"
                      />
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {isPreviewOpen && (
            <div className="lg:col-span-1">
              <JsonPreview 
                payload={{
                  meta: {
                    title: testCase.title,
                    preset_type: presetType,
                    created_date: new Date().toISOString(),
                    user_oid: config.user_id || '',
                    user_name: [config.first_name, config.last_name].filter(Boolean).join(' ').trim() || '',
                    user_email: config.email || '',
                    auth_status: testCase.scenario_auth || 'anonymous',
                    state: config.state || '',
                    planning_pathway: config?.project?.planning_pathway || '',
                    council_name: config?.project?.council_name || '',
                    collections: collectionsText.split(',').map(s => s.trim()).filter(Boolean),
                  },
                  title: testCase.title,
                  chat_input: testCase.user_input,
                  expected_answer: testCase.expected_response_answer,
                  expected_question: testCase.expected_response_question,
                  actual_answer: testCase.actual_response_answer,
                  actual_question: testCase.actual_response_question,
                  user_config: config,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
